import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Users, Package, MapTrifold, CurrencyDollar, Plus, Trash, Receipt, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";

const PIE_COLORS = ["#2E523A", "#E8B291", "#4A6E7C", "#8FA98A", "#C98A5E"];
const CHART_TOOLTIP_STYLE = { background: "#171A18", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" };
const BAR_RADIUS = [6, 6, 0, 0];

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#171A18] border border-white/10 rounded-2xl p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#8B8E85]">{label}</span>
        <Icon size={22} weight="duotone" className="text-[#E8B291]" />
      </div>
      <div className="font-display text-4xl font-bold text-[#FAFAF7] mt-3">{value}</div>
    </div>
  );
}

const TABS = [
  { id: "overview", label: "Overview", icon: ChartBar },
  { id: "products", label: "Products", icon: Package },
  { id: "orders", label: "Orders", icon: Receipt },
  { id: "users", label: "Users", icon: Users },
];

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", price: "", category: "Gear", description: "", image: "" });

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  const loadOrders = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  const loadUsers = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});

  useEffect(() => { loadStats(); loadProducts(); loadOrders(); loadUsers(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const addProduct = async () => {
    if (!form.name || !form.price || !form.image) { toast.error("Fill in name, price and image"); return; }
    try {
      await api.post("/admin/products", { ...form, price: parseFloat(form.price) });
      toast.success("Product added");
      setOpen(false);
      setForm({ name: "", price: "", category: "Gear", description: "", image: "" });
      loadProducts(); loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };

  const deleteProduct = async (lk) => {
    await api.delete(`/admin/products/${lk}`);
    toast.success("Product removed");
    loadProducts(); loadStats();
  };

  return (
    <div className="min-h-screen bg-[#0F1110] text-[#FAFAF7] pt-16 relative">
      <div className="noise-overlay" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#E8B291]">Control room</span>
            <h1 className="font-display text-4xl font-bold mt-1">Admin Dashboard</h1>
          </div>
          <span className="text-sm text-[#8B8E85]">Signed in as {user?.name}</span>
        </div>

        <div className="flex gap-1 border-b border-white/10 mb-8 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors duration-200 ${
                tab === t.id ? "border-[#E8B291] text-[#FAFAF7]" : "border-transparent text-[#8B8E85] hover:text-[#FAFAF7]"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              <t.icon size={18} weight="duotone" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && stats && (
          <div className="space-y-6" data-testid="admin-overview">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={CurrencyDollar} label="Revenue" value={`$${stats.revenue}`} />
              <StatCard icon={Receipt} label="Paid Orders" value={stats.paid_orders} />
              <StatCard icon={Users} label="Users" value={stats.users} />
              <StatCard icon={MapTrifold} label="Trip Boards" value={stats.boards} />
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="bg-[#171A18] border border-white/10 rounded-2xl p-6">
                <h3 className="font-display text-xl font-bold mb-4">Destinations by category</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={stats.listings_by_category}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2e2b" />
                    <XAxis dataKey="category" stroke="#8B8E85" fontSize={12} />
                    <YAxis stroke="#8B8E85" fontSize={12} allowDecimals={false} />
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                    <Bar dataKey="count" fill="#E8B291" radius={BAR_RADIUS} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-[#171A18] border border-white/10 rounded-2xl p-6">
                <h3 className="font-display text-xl font-bold mb-4">Revenue by category</h3>
                {stats.revenue_by_category.length === 0 ? (
                  <p className="text-[#B4B7AE] text-sm py-20 text-center">No paid orders yet.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={stats.revenue_by_category} dataKey="revenue" nameKey="category" cx="50%" cy="50%" outerRadius={90} label={(e) => e.category}>
                        {stats.revenue_by_category.map((entry, i) => (<Cell key={entry.category} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
                      </Pie>
                      <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "products" && (
          <div data-testid="admin-products">
            <div className="flex justify-end mb-4">
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full bg-[#E8B291] text-[#1A1C19] hover:bg-[#d99f79]" data-testid="admin-add-product-btn"><Plus size={18} weight="bold" /> Add product</Button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl bg-white text-[#1A1C19]">
                  <DialogHeader><DialogTitle className="font-display text-2xl">New product</DialogTitle></DialogHeader>
                  <div className="space-y-3 py-2">
                    <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label>Price ($)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price-input" /></div>
                      <div className="space-y-1.5"><Label>Category</Label>
                        <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-category-select">
                          <option>Bags</option><option>Apparel</option><option>Gear</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-1.5"><Label>Image URL</Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="product-image-input" /></div>
                    <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-description-input" /></div>
                  </div>
                  <DialogFooter><Button onClick={addProduct} className="rounded-full bg-[#2E523A] hover:bg-[#26442f] w-full" data-testid="save-product-btn">Save product</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="bg-[#171A18] border border-white/10 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[#8B8E85] border-b border-white/10">
                  <th className="px-5 py-4 font-semibold">Product</th><th className="px-5 py-4 font-semibold">Category</th><th className="px-5 py-4 font-semibold">Price</th><th className="px-5 py-4"></th>
                </tr></thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.lookup_key} className="border-b border-white/5" data-testid={`admin-product-row-${p.lookup_key}`}>
                      <td className="px-5 py-3"><div className="flex items-center gap-3"><img src={p.image} alt="" className="h-10 w-10 rounded-lg object-cover" /><span className="font-medium">{p.name}</span></div></td>
                      <td className="px-5 py-3 text-[#C9CCC4]">{p.category}</td>
                      <td className="px-5 py-3 font-display font-bold text-[#E8B291]">${p.price}</td>
                      <td className="px-5 py-3 text-right"><button onClick={() => deleteProduct(p.lookup_key)} className="p-2 rounded-full hover:bg-white/10 text-[#8B8E85] hover:text-destructive transition-colors duration-200" data-testid={`admin-delete-product-${p.lookup_key}`}><Trash size={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div className="bg-[#171A18] border border-white/10 rounded-2xl overflow-hidden" data-testid="admin-orders">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[#8B8E85] border-b border-white/10">
                <th className="px-5 py-4 font-semibold">Session</th><th className="px-5 py-4 font-semibold">Customer</th><th className="px-5 py-4 font-semibold">Amount</th><th className="px-5 py-4 font-semibold">Status</th>
              </tr></thead>
              <tbody>
                {orders.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-[#B4B7AE]">No orders yet.</td></tr>}
                {orders.map((o) => (
                  <tr key={o.session_id} className="border-b border-white/5" data-testid={`admin-order-row`}>
                    <td className="px-5 py-3 font-mono text-xs text-[#C9CCC4]">{o.session_id.slice(0, 20)}…</td>
                    <td className="px-5 py-3">{o.user_email || "Guest"}</td>
                    <td className="px-5 py-3 font-display font-bold text-[#E8B291]">${(o.amount || 0).toFixed(2)}</td>
                    <td className="px-5 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${o.payment_status === "paid" ? "bg-[#2E523A] text-white" : "bg-white/10 text-[#C9CCC4]"}`}>{o.payment_status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "users" && (
          <div className="bg-[#171A18] border border-white/10 rounded-2xl overflow-hidden" data-testid="admin-users">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[#8B8E85] border-b border-white/10">
                <th className="px-5 py-4 font-semibold">Name</th><th className="px-5 py-4 font-semibold">Email</th><th className="px-5 py-4 font-semibold">Role</th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-white/5" data-testid="admin-user-row">
                    <td className="px-5 py-3 font-medium">{u.name}</td>
                    <td className="px-5 py-3 text-[#C9CCC4]">{u.email}</td>
                    <td className="px-5 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${u.role === "admin" ? "bg-[#E8B291] text-[#1A1C19]" : "bg-white/10 text-[#C9CCC4]"}`}>{u.role}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
